import { apiClient } from './api';
import type {
    GeoCoords,
    SecurityPinRegenerateResponse,
    SecurityPinResponse,
    SecurityPinVerifyResponse,
    PodMeta,
    TrackingContractPod,
    TrackingEta,
    TrackingEtaResponse,
    TrackingEventsResponse,
    TrackingEvent,
    TrackingFlow,
    TrackingFlowReplyMutationResponse,
    TrackingFlowReplyPayload,
    TrackingFlowResponse,
    TrackingFlowStepPayload,
    TrackingPodConfirmResponse,
    TrackingPodDownloadResult,
    TrackingPodRejectResponse,
    TrackingPodResponse,
    TrackingPodUploadResponse,
    TrackingState,
    TrackingStateResponse,
} from '../types/tracking';

function extractFilenameFromContentDisposition(contentDispositionHeader: string | null): string | null {
    if (!contentDispositionHeader) {
        return null;
    }

    const utf8FilenameMatch = contentDispositionHeader.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8FilenameMatch?.[1]) {
        try {
            return decodeURIComponent(utf8FilenameMatch[1]);
        } catch {
            return utf8FilenameMatch[1];
        }
    }

    const quotedFilenameMatch = contentDispositionHeader.match(/filename="([^"]+)"/i);
    if (quotedFilenameMatch?.[1]) {
        return quotedFilenameMatch[1];
    }

    const rawFilenameMatch = contentDispositionHeader.match(/filename=([^;]+)/i);
    if (rawFilenameMatch?.[1]) {
        return rawFilenameMatch[1].trim();
    }

    return null;
}

export const trackingService = {
    async getTracking(contractId: number): Promise<TrackingState> {
        const response = await apiClient.get<TrackingStateResponse>(`/api/contracts/${contractId}/tracking`);
        return response.data;
    },

    async startTracking(contractId: number): Promise<TrackingState> {
        const response = await apiClient.post<TrackingStateResponse>(
            `/api/contracts/${contractId}/tracking/start`,
            {},
        );

        return response.data;
    },

    async getEvents(contractId: number): Promise<TrackingEvent[]> {
        const response = await apiClient.get<TrackingEventsResponse>(`/api/contracts/${contractId}/tracking/events`);
        return response.data;
    },

    async getEta(contractId: number): Promise<TrackingEta | null> {
        const response = await apiClient.get<TrackingEtaResponse>(`/api/contracts/${contractId}/tracking/eta`);
        return response.data;
    },

    async getFlow(contractId: number): Promise<TrackingFlow> {
        const response = await apiClient.get<TrackingFlowResponse>(`/api/contracts/${contractId}/tracking/flow`);
        return response.data;
    },

    async markOriginArrived(contractId: number, location?: GeoCoords): Promise<TrackingState> {
        const response = await apiClient.post<TrackingStateResponse>(
            `/api/contracts/${contractId}/tracking/origin-arrived`,
            location
                ? {
                    latitude: location.latitude,
                    longitude: location.longitude,
                }
                : {},
        );

        return response.data;
    },

    async markCargoLoaded(contractId: number): Promise<TrackingState> {
        const response = await apiClient.post<TrackingStateResponse>(
            `/api/contracts/${contractId}/tracking/cargo-loaded`,
            {},
        );

        return response.data;
    },

    async markTransportStarted(contractId: number): Promise<TrackingState> {
        const response = await apiClient.post<TrackingStateResponse>(
            `/api/contracts/${contractId}/tracking/transport-started`,
            {},
        );

        return response.data;
    },

    async markDestinationArrived(contractId: number, location?: GeoCoords): Promise<TrackingState> {
        const response = await apiClient.post<TrackingStateResponse>(
            `/api/contracts/${contractId}/tracking/destination-arrived`,
            location
                ? {
                    latitude: location.latitude,
                    longitude: location.longitude,
                }
                : {},
        );

        return response.data;
    },

    async triggerFlowStep(
        contractId: number,
        stepKey: string,
        payload: TrackingFlowStepPayload,
    ): Promise<TrackingFlow> {
        const response = await apiClient.post<TrackingFlowResponse>(
            `/api/contracts/${contractId}/tracking/flow/steps/${stepKey}`,
            payload,
        );

        return response.data;
    },

    async replyFlowState(
        contractId: number,
        flowStateId: number,
        payload: TrackingFlowReplyPayload,
    ): Promise<TrackingFlowReplyMutationResponse['data']> {
        const response = await apiClient.post<TrackingFlowReplyMutationResponse>(
            `/api/contracts/${contractId}/tracking/flow/states/${flowStateId}/replies`,
            payload,
        );

        return response.data;
    },

    async getSecurityPin(contractId: number, type: 'pickup' | 'delivery'): Promise<SecurityPinResponse> {
        return apiClient.getQuiet<SecurityPinResponse>(`/api/contracts/${contractId}/security-pins/${type}`);
    },

    async regenerateSecurityPin(
        contractId: number,
        type: 'pickup' | 'delivery',
        reason: string = 'manual_request',
    ): Promise<SecurityPinRegenerateResponse> {
        return apiClient.post<SecurityPinRegenerateResponse>(
            `/api/contracts/${contractId}/security-pins/${type}/regenerate`,
            { reason },
        );
    },

    async verifySecurityPin(
        contractId: number,
        type: 'pickup' | 'delivery',
        payload: { pin?: string; qr_token?: string },
    ): Promise<SecurityPinVerifyResponse> {
        return apiClient.post<SecurityPinVerifyResponse>(
            `/api/contracts/${contractId}/security-pins/${type}/verify`,
            payload,
        );
    },

    async getPod(contractId: number): Promise<TrackingContractPod | null> {
        try {
            const response = await apiClient.getQuiet<TrackingPodResponse>(`/api/contracts/${contractId}/pod`);
            return response.data;
        } catch (error) {
            const apiError = error as { message?: string } | undefined;

            if (typeof apiError?.message === 'string' && apiError.message.toLowerCase().includes('pod no encontrado')) {
                return null;
            }

            throw error;
        }
    },

    async uploadPod(
        contractId: number,
        files: File[],
        data: PodMeta = {},
        onProgress?: (progressPercent: number) => void,
    ): Promise<TrackingContractPod> {
        const formData = new FormData();

        for (const file of files) {
            formData.append('files[]', file);
        }

        if (data.recipient_name) {
            formData.append('recipient_name', data.recipient_name);
        }

        if (data.notes) {
            formData.append('notes', data.notes);
        }

        const response = onProgress
            ? await apiClient.postFormWithProgress<TrackingPodUploadResponse>(
                `/api/contracts/${contractId}/pod`,
                formData,
                onProgress,
            )
            : await apiClient.postForm<TrackingPodUploadResponse>(`/api/contracts/${contractId}/pod`, formData);
        return response.data;
    },

    async confirmPod(contractId: number, podId: number): Promise<TrackingContractPod> {
        const response = await apiClient.post<TrackingPodConfirmResponse>(
            `/api/contracts/${contractId}/pod/${podId}/confirm`,
            {},
        );

        return response.data;
    },

    async rejectPod(contractId: number, podId: number, reason: string): Promise<TrackingContractPod> {
        const response = await apiClient.post<TrackingPodRejectResponse>(
            `/api/contracts/${contractId}/pod/${podId}/reject`,
            { reason },
        );

        return response.data;
    },

    async downloadPodFile(contractId: number, fileId: number): Promise<TrackingPodDownloadResult> {
        const { blob, headers } = await apiClient.getBlob(`/api/contracts/${contractId}/pod/files/${fileId}/download`);
        const contentDisposition = headers.get('content-disposition');
        const fileName = extractFilenameFromContentDisposition(contentDisposition) || `contrato-${contractId}-pod-${fileId}`;

        return {
            blob,
            fileName,
        };
    },
};
